import { CSSProperties, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { gainSelector } from '../../layout/selectors/layout.selectors';
import { getLinePositions, useWebGL } from '../hooks/useWebGL';
import { Cluster } from '../../clustering/clustering.types';
import { Dimension } from '../types/web-gl.type';

interface Props {
  width: number;
  height: number;
  canvasWidth: number;
  canvasHeight: number;
  style: CSSProperties;
  xAxis: { xMin?: number; xMax: number };
  yAxis: { yMin?: number; yMax: number };
  clusters: Cluster[];
  scroll: { x: number; y: number };
  dimensions: Record<number, Dimension>;
}

export const WebGLContainer: React.FC<Props> = ({
  width,
  height,
  canvasWidth,
  canvasHeight,
  style,
  xAxis: { xMin = 0, xMax },
  yAxis: { yMin = 0, yMax },
  clusters,
  scroll,
  dimensions,
}) => {
  const [canvasRef, setCanvasRef] = useState<HTMLCanvasElement | null>(null);
  const gain = useSelector(gainSelector);

  const positions = useMemo(() => {
    if (
      clusters.length !== 0 &&
      Object.keys(dimensions).length === clusters.length &&
      canvasRef != null
    ) {
      return getLinePositions(clusters, dimensions, canvasRef);
    }
    return new Float32Array([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clusters, dimensions, canvasRef]);

  const { attributes, gl, draw, addTranslation, addGain, drawBackground } =
    useWebGL(canvasRef);

  useEffect(() => {
    if (attributes != null && gl != null && canvasRef != null) {
      const {
        translationUniformLocation,
        domainMinUniformLocation,
        domainMaxUniformLocation,
        rangeMaxUniformLocation,
        rangeMinUniformLocation,
        shaderProgram,
        positionAttrLocation,
        initialPaddingAttrLocation,
        gainUniformLocation,
        containerUniformLocation,
      } = attributes;

      drawBackground(gl, canvasRef, dimensions, width, height, scroll);
      draw(
        positions,
        gl,
        shaderProgram,
        positionAttrLocation,
        initialPaddingAttrLocation,
        domainMinUniformLocation,
        domainMaxUniformLocation,
        rangeMaxUniformLocation,
        rangeMinUniformLocation,
        containerUniformLocation,
        translationUniformLocation,
        gainUniformLocation,
        {
          range: {
            min: {},
            max: {
              x: width,
              y: height,
            },
          },
        },
        {
          domain: {
            min: {
              x: xMin,
              y: yMax,
            },
            max: {
              x: xMax,
              y: yMin,
            },
          },
        },
        gain,
        {
          x: scroll.x / canvasWidth,
          y: scroll.y / canvasHeight,
        },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    attributes,
    draw,
    gl,
    positions,
    dimensions,
    canvasRef,
    canvasHeight,
    canvasWidth,
  ]);

  useEffect(() => {
    if (
      attributes != null &&
      gl != null &&
      positions.length !== 0 &&
      canvasRef != null
    ) {
      const { gainUniformLocation } = attributes;
      drawBackground(gl, canvasRef, dimensions, width, height, scroll);
      addGain(gl, gainUniformLocation, gain, positions.length);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gain]);

  useEffect(() => {
    if (
      attributes != null &&
      gl != null &&
      positions.length !== 0 &&
      canvasRef != null
    ) {
      const { translationUniformLocation } = attributes;
      drawBackground(gl, canvasRef, dimensions, width, height, scroll);
      addTranslation(
        gl,
        translationUniformLocation,
        {
          x: scroll.x / canvasWidth,
          y: scroll.y / canvasHeight,
        },
        positions.length,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scroll]);

  return (
    <canvas
      ref={setCanvasRef}
      width={canvasWidth}
      height={canvasHeight}
      style={style}
    />
  );
};
